import React, { FC, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';

import { FeatureFlag } from 'app/api/services/site-configs/types';
import { ReduxState } from 'app/redux/types';

import { API, useFetch } from 'app/api';
import { SsrModule } from 'app/constants/ssrModules';
import { Skeleton } from 'app/design-system/components/Skeleton';
import { useTranslation } from 'app/lang';
import { asyncQueue } from 'app/lib/asyncQueue';
import { useFeatureFlag } from 'app/lib/feature-flags/useFeatureFlag';
import { selectSignalrConnected, subscribeToMatches, unsubscribeFromMatches } from 'app/lib/signalr';
import { useComponentWillReceiveProps } from 'app/packages/lib/hooks';

import { MATCH_OF_THE_DAY_TOPIC } from './actions';
import Carousel from './components/Carousel';
import { entityLoading, entityRequested } from './selectors/entities';
import getMatchIdsFromBanners from './utils/get-match-ids-from-banners';

// ---

type StoreProps = {
  signalrConnected: boolean | null;
  loading: boolean;
  requested: boolean;
};

type DispatchProp = {
  dispatch: Dispatch;
};

const MatchOfTheDay: FC<StoreProps & DispatchProp> = ({
  signalrConnected,
  loading,
  requested,
  dispatch,
}) => {
  const { lang } = useTranslation();
  const isActiveBannersPreview = useFeatureFlag(FeatureFlag.BANNERS_PREVIEW);

  const {
    data: bannersData,
    loading: bannersLoading,
    requested: bannerRequested,
    makeRequest: makeBannerRequest,
  } = useFetch(
    API.strapiServices.banners.getBanners(isActiveBannersPreview, 'banner'),
    { ssrUniqKey: 'strapi-banners' },
  );

  const matchIds = useMemo(() => getMatchIdsFromBanners(bannersData), [bannersData]);

  const isMatchesLoading = (matchIds.length > 0 && !requested) || loading;
  const isSkeleton = isMatchesLoading || bannersLoading;

  useEffect(() => {
    if (!bannerRequested) {
      makeBannerRequest();
    }
  }, []);

  useComponentWillReceiveProps(() => {
    makeBannerRequest();
  }, [lang]);

  useEffect(() => {
    if (!signalrConnected || !matchIds.length) {
      return;
    }

    if (requested && !loading) {
      asyncQueue.removeFromQueue(MATCH_OF_THE_DAY_TOPIC, () => {
        dispatch(unsubscribeFromMatches(MATCH_OF_THE_DAY_TOPIC));
      });
    }

    asyncQueue.addToQueue(MATCH_OF_THE_DAY_TOPIC, () => {
      dispatch(subscribeToMatches(MATCH_OF_THE_DAY_TOPIC, matchIds));
    });
  }, [signalrConnected, matchIds]);

  if (isSkeleton) {
    return (
      <Skeleton width="100%"
                height={305} />
    );
  }

  if (matchIds.length <= 0 && !bannersData?.length) {
    return null;
  }

  return (
    <div data-ssr-id={SsrModule.MATCH_OF_THE_DAY}>
      <Carousel banners={bannersData || []} />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  signalrConnected: selectSignalrConnected(state),
  loading: entityLoading(state),
  requested: entityRequested(state),
});

export default connect(mapStateToProps)(MatchOfTheDay);
